/**
 * Quvur gidravlikasi.
 *
 * MANBA: Kasatkin 2-bob (gidravlik qarshilik, Darcy-Weisbach);
 *        Pavlov-Romankov-Noskov, 1-bob (gidravlika misollari);
 *        Churchill S.W., "Friction-factor equation spans all fluid-flow regimes",
 *          Chem. Eng., 84 (24), 91–92 (1977);
 *        Idelchik I.E., "Spravochnik po gidravlicheskim soprotivleniyam" (mahalliy K);
 *        Perry's 8th ed., Sec. 6 (Fluid and Particle Dynamics).
 *
 * TENGLAMALAR
 *   Re   = ρ·v·d/μ,   v = Q/A,  A = π d²/4
 *   Darcy-Weisbach:  h_f = f · (L/d) · v²/(2g)
 *   Churchill (1977), Darcy f (barcha rejimlar, uzluksiz):
 *      f = 8 · [ (8/Re)^12 + 1/(A + B)^1.5 ]^(1/12)
 *      A = [ 2.457 · ln( 1 / ( (7/Re)^0.9 + 0.27·ε/d ) ) ]^16,   B = (37530/Re)^16
 *      Re → 0 da f → 64/Re (Hagen-Poiseuille); turbulentda Colebrook ga ±~3%.
 *   Mahalliy:  h_m = ΣK · v²/(2g)
 *   Ventil:    h_v = K_v(opening) · v²/(2g)   (types.ts, ValveParams)
 *   Rejim: Re ≤ 2300 laminar, 2300 < Re < 4000 o'tish, Re ≥ 4000 turbulent (Kasatkin)
 *
 * ASSUMPTION:
 *   1. Suyuqlik siqilmas, xossalar (ρ, μ) kirish haroratida, izotermik oqim.
 *   2. Kinetik energiya farqi idishlar orasida 0 (ikkala idish sathi tinch).
 *   3. Mahalliy qarshiliklar K tezlikka bog'liq emas (turbulent qiymatlar, Idelchik).
 *   4. Ventil K ochilishga bog'liq, Re ga bog'liq emas.
 *   5. Yo'nalish bo'yicha: h_f ∝ v·|v| (teskari oqimda ishora almashadi — dinamika uchun).
 */
import type { FlowRegime, PipeSegment, ValveParams } from './types';

export const G = 9.80665;

export function pipeArea(d: number): number {
  return (Math.PI * d * d) / 4;
}

export function reynolds(rho: number, v: number, d: number, mu: number): number {
  return (rho * Math.abs(v) * d) / mu;
}

export function flowRegime(Re: number): FlowRegime {
  if (Re <= 2300) return 'laminar';
  if (Re < 4000) return 'transitional';
  return 'turbulent';
}

/** Churchill (1977) Darcy ishqalanish koeffitsienti. Re = 0 → Infinity (h_f = 0 baribir). */
export function frictionFactorChurchill(Re: number, relativeRoughness: number): number {
  if (!(Re > 0)) return Infinity;
  const A = Math.pow(2.457 * Math.log(1 / (Math.pow(7 / Re, 0.9) + 0.27 * relativeRoughness)), 16);
  const B = Math.pow(37530 / Re, 16);
  return 8 * Math.pow(Math.pow(8 / Re, 12) + 1 / Math.pow(A + B, 1.5), 1 / 12);
}

/** Ventil qarshilik koeffitsienti K_v(opening). opening ≤ 0 → Infinity (yopiq). */
export function valveK(valve: ValveParams, opening: number): number {
  if (!(opening > 0)) return Infinity;
  const op = Math.min(1, opening);
  if (valve.characteristic === 'quadratic') return valve.K0 / (op * op);
  const R = valve.rangeability ?? 50;
  return valve.K0 * Math.pow(R, 2 * (1 - op));
}

export interface SegmentLoss {
  /** m/s (ishorali) */
  velocity: number;
  Re: number;
  f: number;
  regime: FlowRegime;
  /** m, ishorali (Q yo'nalishi bo'yicha) */
  hFriction: number;
  /** m, ishorali */
  hMinor: number;
}

/** Bir quvur bo'lagi yo'qotishlari (ishorali: Q < 0 → manfiy). */
export function segmentLoss(seg: PipeSegment, Q: number, rho: number, mu: number): SegmentLoss {
  const v = Q / pipeArea(seg.diameter);
  const Re = reynolds(rho, v, seg.diameter, mu);
  const f = frictionFactorChurchill(Re, seg.roughness / seg.diameter);
  const head = (v * Math.abs(v)) / (2 * G);
  const hFriction = Re > 0 ? f * (seg.length / seg.diameter) * head : 0;
  return { velocity: v, Re, f, regime: flowRegime(Re), hFriction, hMinor: seg.sumK * head };
}

export function validateSegment(name: string, s: PipeSegment): void {
  if (!(s.length > 0)) throw new Error(`pump-pipeline: ${name}.length must be > 0`);
  if (!(s.diameter > 0)) throw new Error(`pump-pipeline: ${name}.diameter must be > 0`);
  if (!(s.roughness >= 0)) throw new Error(`pump-pipeline: ${name}.roughness must be ≥ 0`);
  if (!(s.sumK >= 0)) throw new Error(`pump-pipeline: ${name}.sumK must be ≥ 0`);
}
